import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaUsers, FaUserTie, FaClipboardCheck, FaSearch, FaCheckCircle, FaTrash } from 'react-icons/fa';
import Toast from '../components/Toast';
import * as authService from '../services/authService';
import * as adminService from '../services/adminService';

const ReviewerManagement = () => {
    const [reviewers, setReviewers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState(null);
    const [selectedReviewer, setSelectedReviewer] = useState(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [actionType, setActionType] = useState('');
    const [viewFilter, setViewFilter] = useState('all'); // 'all', 'reviewers', 'candidates', 'unauthorized'
    const [unauthorizedReviewerIds, setUnauthorizedReviewerIds] = useState(new Set());
    const navigate = useNavigate();

    // Check if user is admin
    useEffect(() => {
        const user = authService.getCurrentUser();
        if (!user) {
            navigate('/login');
            return;
        }
        if (user.role !== 'admin') {
            navigate('/');
            return;
        }

        loadReviewers();
    }, [navigate]);

    const loadReviewers = async () => {
        setLoading(true);

        try {
            // Fetch data from endpoints
            const [professorsRes, editorialRes, reviewersRes, unauthorizedRes] = await Promise.all([
                adminService.getProfessors(),
                adminService.getEditorialAssignments(),
                adminService.getReviewers(),
                adminService.getUnauthorizedReviewers()
            ]);

            // Normalize responses
            const allProfessors = professorsRes.data || professorsRes;
            const editorialBoard = editorialRes.data || editorialRes;
            const reviewersList = reviewersRes.data || reviewersRes;
            const unauthorizedList = unauthorizedRes.data || unauthorizedRes;

            // Create lookup sets for IDs
            const editorialIds = new Set((Array.isArray(editorialBoard) ? editorialBoard : []).map(item => item.professor_id || item.user_id || item.id));
            const reviewerIds = new Set((Array.isArray(reviewersList) ? reviewersList : []).map(item => item.professor_id || item.user_id || item.id));
            const unauthorizedIds = new Set((Array.isArray(unauthorizedList) ? unauthorizedList : []).map(item => item.professor_id || item.user_id || item.id));

            setUnauthorizedReviewerIds(unauthorizedIds);

            // Merge data
            const processorsWithRoles = (Array.isArray(allProfessors) ? allProfessors : []).map(prof => {
                const userData = prof.user || {};
                const id = prof.user_id || prof.id;
                return {
                    ...prof,
                    name: userData.full_name || prof.full_name || userData.name || prof.name || 'Unknown',
                    email: userData.email || prof.email || 'N/A',
                    phone: userData.phone_number || prof.phone_number || prof.phone || 'N/A',
                    id: id,
                    editorialBoard: prof.is_editorial_board !== undefined ? prof.is_editorial_board : editorialIds.has(id),
                    reviewer: prof.is_reviewer !== undefined ? prof.is_reviewer : reviewerIds.has(id),
                    isUnauthorized: unauthorizedIds.has(id)
                };
            });

            // Do NOT filter yet - we want to show candidates too
            setReviewers(processorsWithRoles);
        } catch (error) {
            console.error('Error loading reviewers:', error);
            showToast('Failed to load reviewer data', 'error');
            setReviewers([]);
        } finally {
            setLoading(false);
        }
    };

    const getFilteredReviewers = () => {
        let filtered = [...reviewers];

        if (searchTerm && searchTerm.trim() !== '') {
            const search = searchTerm.toLowerCase().trim();
            filtered = filtered.filter(rev => {
                const name = (rev.name || '').toLowerCase();
                const email = (rev.email || '').toLowerCase();
                const phone = (rev.phone || rev.phone_number || '').toLowerCase();
                return name.includes(search) || email.includes(search) || phone.includes(search);
            });
        }

        return filtered;
    };

    const filteredReviewers = getFilteredReviewers();
    // Requirements: once assigned to editorial board, they move to editorial management.
    // So here we only show those who are NOT in editorial board.
    const relevantReviewers = filteredReviewers.filter(r => r.editorialBoard === false);

    const assignedReviewers = relevantReviewers.filter(r => r.reviewer === true);
    let candidateReviewers = relevantReviewers.filter(r => r.reviewer === false);

    // Prepare lists based on viewFilter
    const showAssigned = viewFilter === 'all' || viewFilter === 'reviewers';
    const showCandidates = viewFilter === 'all' || viewFilter === 'candidates' || viewFilter === 'unauthorized';

    // Further filter candidates if 'unauthorized' is selected
    const displayCandidates = viewFilter === 'unauthorized'
        ? candidateReviewers.filter(r => r.isUnauthorized === true)
        : candidateReviewers;

    const displayAssigned = showAssigned ? assignedReviewers : [];
    const finalCandidateList = showCandidates ? displayCandidates : [];

    const showToast = (message, type = 'success') => {
        setToast({ message, type });
    };

    const handleAction = (reviewer, type) => {
        setSelectedReviewer(reviewer);
        setActionType(type);
        setShowConfirmModal(true);
    };

    const confirmAction = async () => {
        if (!selectedReviewer) return;

        try {
            if (actionType === 'reviewer') {
                const isCurrentlyAssigned = selectedReviewer.reviewer;
                if (isCurrentlyAssigned) {
                    await adminService.removeFromReviewers(selectedReviewer.id);
                    showToast(`${selectedReviewer.name} removed from Reviewers`, 'success');
                } else {
                    await adminService.assignAsReviewer({ professor_id: selectedReviewer.id });
                    showToast(`${selectedReviewer.name} assigned as Reviewer`, 'success');
                }
            } else if (actionType === 'editorial') {
                await adminService.assignToEditorialBoard({ professor_id: selectedReviewer.id });
                showToast(`${selectedReviewer.name} assigned to Editorial Board and moved to Editorial Management`, 'success');
            } else if (actionType === 'reject') {
                await adminService.rejectUnauthorizedReviewer(selectedReviewer.id);
                showToast(`${selectedReviewer.name} application rejected`, 'success');
            }

            await loadReviewers();

        } catch (error) {
            console.error('Error updating role:', error);
            showToast(error.message || 'Error updating role', 'error');
        } finally {
            setShowConfirmModal(false);
            setSelectedReviewer(null);
            setActionType('');
        }
    };

    const darkGradient = {
        background: 'linear-gradient(135deg, rgb(15, 23, 42) 0%, rgb(49, 46, 129) 50%, rgb(15, 23, 42) 100%)'
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600 font-medium">Loading reviewers...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-8 px-4">
            {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

            {/* Confirmation Modal */}
            {showConfirmModal && selectedReviewer && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
                        <div className="text-center mb-6">
                            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <FaClipboardCheck className="w-8 h-8 text-blue-600" />
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-2">Confirm Action</h3>
                            <p className="text-gray-600">
                                {actionType === 'editorial'
                                    ? `Promote ${selectedReviewer.name} to Editorial Board? (This will move them to Editorial Management)`
                                    : (actionType === 'reject'
                                        ? `Reject ${selectedReviewer.name}'s application? This will remove them from reviewer management.`
                                        : (selectedReviewer.reviewer
                                            ? `Remove ${selectedReviewer.name} from Reviewers?`
                                            : `Assign ${selectedReviewer.name} as Reviewer?`))
                                }
                            </p>
                        </div>
                        <div className="flex gap-3">
                            <button
                                onClick={() => { setShowConfirmModal(false); setSelectedReviewer(null); }}
                                className="flex-1 px-4 py-3 bg-gray-200 text-gray-800 rounded-lg font-semibold hover:bg-gray-300 transition-all"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmAction}
                                className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg font-semibold hover:shadow-lg transition-all"
                            >
                                Confirm
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="max-w mx-auto">
                {/* Header */}
                <div className="rounded-2xl shadow-lg p-6 mb-6" style={darkGradient}>
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                                <FaClipboardCheck className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <h1 className="text-3xl font-bold text-white">Reviewer Management</h1>
                                <p className="text-blue-100 mt-1">Manage all assigned reviewers and candidates</p>
                            </div>
                        </div>
                        <div className="text-right">
                            <p className="text-sm text-blue-200">Total Professors</p>
                            <p className="text-3xl font-bold text-white">{reviewers.length}</p>
                        </div>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                        <div className="p-4 bg-green-50 rounded-xl border border-green-100 flex items-center justify-between">
                            <div>
                                <p className="text-sm text-green-600 font-semibold uppercase">Assigned</p>
                                <p className="text-2xl font-bold text-green-900">{assignedReviewers.length}</p>
                            </div>
                            <FaCheckCircle className="text-3xl text-green-500" />
                        </div>
                        <div className="p-4 bg-amber-50 rounded-xl border border-amber-100 flex items-center justify-between">
                            <div>
                                <p className="text-sm text-amber-600 font-semibold uppercase">Candidates</p>
                                <p className="text-2xl font-bold text-amber-900">{candidateReviewers.length}</p>
                            </div>
                            <FaUsers className="text-3xl text-amber-500" />
                        </div>
                    </div>
                </div>

                {/* Search */}
                <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
                    <div className="flex flex-col md:flex-row gap-4">
                        <div className="relative flex-1">
                            <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search by name, email, or phone..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-600 focus:border-transparent"
                            />
                        </div>
                        <div className="md:w-64">
                            <select
                                value={viewFilter}
                                onChange={(e) => setViewFilter(e.target.value)}
                                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-600 focus:border-transparent bg-white text-gray-700 font-medium"
                            >
                                <option value="all">View Everything</option>
                                <option value="reviewers">Assigned Reviewers</option>
                                <option value="candidates">All Candidates</option>
                                <option value="unauthorized">Unauthorized Only</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Reviewers List */}
                <div className="space-y-8">
                    {/* Assigned Section */}
                    {displayAssigned.length > 0 && (
                        <div>
                            <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                                <FaCheckCircle className="text-green-600" />
                                Assigned Reviewers
                            </h2>
                            <div className="space-y-4">
                                {displayAssigned.map((reviewer, index) => (
                                    <div key={`assigned-${reviewer.email}-${index}`} className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300">
                                        <div className="p-6">
                                            <div className="flex flex-col lg:flex-row lg:items-center gap-6">
                                                <div className="flex-1">
                                                    <div className="flex items-start gap-4">
                                                        <div className="w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0" style={darkGradient}>
                                                            <span className="text-2xl font-bold text-white">
                                                                {reviewer.name.charAt(0).toUpperCase()}
                                                            </span>
                                                        </div>
                                                        <div className="flex-1">
                                                            <h3 className="text-xl font-bold text-gray-900 mb-1">{reviewer.name}</h3>
                                                            <p className="text-gray-600 text-sm mb-2">{reviewer.email}</p>
                                                            {reviewer.phone && <p className="text-gray-500 text-sm">📞 {reviewer.phone}</p>}
                                                            <div className="mt-2 flex gap-2">
                                                                <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold">Reviewer</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex flex-col sm:flex-row gap-3 lg:flex-col lg:w-64">

                                                    <button
                                                        onClick={() => handleAction(reviewer, 'editorial')}
                                                        className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold bg-blue-50 text-blue-600 border-2 border-blue-200 hover:bg-blue-100 transition-all duration-300"
                                                    >
                                                        <FaUserTie className="w-4 h-4" />
                                                        <span>Add to Editorial</span>
                                                    </button>
                                                    <button
                                                        onClick={() => handleAction(reviewer, 'reviewer')}
                                                        className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold bg-red-50 text-red-600 border-2 border-red-200 hover:bg-red-100 transition-all duration-300"
                                                    >
                                                        <FaTrash className="w-4 h-4" />
                                                        <span>Revoke Rights</span>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Candidates Section */}
                    {finalCandidateList.length > 0 && (
                        <div>
                            <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                                <FaUsers className="text-amber-600" />
                                {viewFilter === 'unauthorized' ? 'Unauthorized Candidates' : 'Reviewer Candidates'}
                            </h2>
                            <div className="space-y-4">
                                {finalCandidateList.map((reviewer, index) => (
                                    <div key={`candidate-${reviewer.email}-${index}`} className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border-l-4 border-amber-400">
                                        <div className="p-6">
                                            <div className="flex flex-col lg:flex-row lg:items-center gap-6">
                                                <div className="flex-1">
                                                    <div className="flex items-start gap-4">
                                                        <div className="w-16 h-16 bg-gradient-to-r from-amber-500 to-amber-600 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold text-2xl shadow-md">
                                                            {reviewer.name.charAt(0).toUpperCase()}
                                                        </div>
                                                        <div className="flex-1">
                                                            <div className="flex items-center gap-2">
                                                                <h3 className="text-xl font-bold text-gray-900">{reviewer.name}</h3>
                                                                {reviewer.isUnauthorized ? (
                                                                    <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-[10px] font-bold uppercase tracking-wider animate-pulse">Unauthorized</span>
                                                                ) : (
                                                                    <span className="px-2 py-0.5 bg-amber-100 text-amber-800 rounded text-[10px] font-bold uppercase tracking-wider">Candidate</span>
                                                                )}
                                                            </div>
                                                            <p className="text-gray-600 text-sm mb-2">{reviewer.email}</p>
                                                            {reviewer.phone && <p className="text-gray-500 text-sm">📞 {reviewer.phone}</p>}
                                                            <div className="mt-2 flex gap-2">
                                                                <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-semibold">Professor</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex flex-col sm:flex-row gap-3 lg:flex-col lg:w-64">
                                                    <button
                                                        onClick={() => handleAction(reviewer, 'reviewer')}
                                                        className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-bold bg-gradient-to-r from-green-500 to-green-600 text-white hover:shadow-lg transform hover:scale-[1.02] transition-all duration-300"
                                                    >
                                                        <FaCheckCircle className="w-5 h-5" />
                                                        <span>Accept</span>
                                                    </button>
                                                    <button
                                                        onClick={() => handleAction(reviewer, 'reject')}
                                                        className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-bold bg-white text-red-600 border-2 border-red-200 hover:bg-red-50 transition-all duration-300"
                                                    >
                                                        <FaTrash className="w-5 h-5" />
                                                        <span>Reject</span>
                                                    </button>

                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {filteredReviewers.length === 0 && (
                        <div className="bg-white rounded-2xl shadow-lg p-12 text-center border-2 border-dashed border-gray-200">
                            <FaClipboardCheck className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Profiles Found</h3>
                            <p className="text-gray-600">Try adjusting your search criteria or clear the filters.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ReviewerManagement;